import { Modal, OverflowTooltip, Table } from "@4uhub/lib4uhub";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { GridColDef } from "@mui/x-data-grid";
import { format } from "date-fns";
import { enUS, es, ptBR } from "date-fns/locale";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ILogError } from "../../../../models/log-error";
import MainApi from "../../../../services/mainApi.service";
import LogErrorDetailsModal from "./LogErrorDetailsModal";
import MacroFilter, { IFilterParams } from "./MacroFilter";

const LOG_ERROR_ROUTE = process.env.REACT_APP_EXTERNAL + "/api/v1/LogError";

const logErrorService = new MainApi<ILogError>(LOG_ERROR_ROUTE);

const translationPath = "page.4uBus.logs.externalLogError.";

const toApiDate = (date: Date | null | undefined) => {
  if (!date) return undefined;

  const normalizedDate = new Date(date);
  normalizedDate.setHours(15);

  return normalizedDate.toISOString().substring(0, 10);
};

const LogError = () => {
  const [refresh, setRefresh] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<ILogError | null>(null);
  const today = useMemo(() => new Date(), []);

  const [filterParams, setFilterParams] = useState<IFilterParams>({
    StartDate: today,
    EndDate: today,
    ErrorCode: "",
  });

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const formatDate = useCallback(
    (value?: string | null) => {
      if (!value) return "-";

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "-";

      return format(date, "Pp", {
        locale: language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es,
      }).replaceAll(",", "");
    },
    [language],
  );

  const renderOverflowCell = useCallback((value?: string | null) => {
    const text = value ?? "-";

    return (
      <OverflowTooltip
        text={text}
        tooltip={text}
        textTransform="none"
        variant="body2"
      />
    );
  }, []);

  const columns: GridColDef<ILogError>[] = useMemo(
    () => [
      {
        field: "id",
        headerName: t(translationPath + "id") || "ID",
        flex: 0.25,
        minWidth: 70,
        maxWidth: 90,
        align: "left",
        headerAlign: "left",
      },
      {
        field: "dherro",
        headerName: t(translationPath + "error_date") || "Error Date",
        flex: 0.55,
        minWidth: 145,
        maxWidth: 170,
        align: "left",
        headerAlign: "left",
        renderCell: (params) => formatDate(params.value),
      },
      {
        field: "errorCode",
        headerName: t(translationPath + "error_code") || "ErrorCode",
        flex: 0.4,
        minWidth: 110,
        maxWidth: 140,
        align: "left",
        headerAlign: "left",
        renderCell: (params) => params.value ?? "-",
      },
      {
        field: "errorMessage",
        headerName: t(translationPath + "error_message") || "Error Message",
        flex: 2.6,
        minWidth: 520,
        align: "left",
        headerAlign: "left",
        renderCell: (params) => renderOverflowCell(params.value),
      },
      {
        field: "sourceClass",
        headerName: t(translationPath + "source_class") || "Source Class",
        flex: 1.8,
        minWidth: 360,
        align: "left",
        headerAlign: "left",
        renderCell: (params) => renderOverflowCell(params.value),
      },
    ],
    [formatDate, renderOverflowCell, t],
  );

  const handleFilterChange = useCallback(
    (newFilters: IFilterParams | ((prev: IFilterParams) => IFilterParams)) => {
      setFilterParams(newFilters);
      setRefresh((prev) => prev + 1);
    },
    [],
  );

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedRow(null);
  }, []);

  return (
    <>
      <Modal open={modalOpen} onClose={handleCloseModal} maxDialogWidth="lg">
        {selectedRow && (
          <LogErrorDetailsModal data={selectedRow} onClose={handleCloseModal} />
        )}
      </Modal>
      <Table<ILogError>
        title={t(translationPath + "title")}
        columns={columns}
        service={logErrorService}
        searchInputPlaceHolder={
          t(translationPath + "search_placeholder") || "Search..."
        }
        showDefaultMacroFilters={false}
        showButton={false}
        showDeleteButton={false}
        defaultActions={true}
        getRowId={(row) => row.id}
        rowActions={(params) => [
          {
            action: () => {
              setSelectedRow(params.row);
              setModalOpen(true);
            },
            icon: <VisibilityIcon fontSize="small" />,
            text: t(translationPath + "view_details"),
          },
        ]}
        serviceProps={useMemo(
          () => ({
            refresh,
            ...filterParams,
            StartDate: toApiDate(filterParams.StartDate),
            EndDate: toApiDate(filterParams.EndDate),
          }),
          [refresh, filterParams],
        )}
        macroFilters={useCallback(
          () => (
            <MacroFilter
              filterParams={filterParams}
              onFilterParams={handleFilterChange}
            />
          ),
          [filterParams, handleFilterChange],
        )}
      />
    </>
  );
};

export default LogError;
